import Vue from 'vue'
import CKEditor from '@ckeditor/ckeditor5-vue2'
import BootstrapVue, { ModalPlugin, ToastPlugin } from 'bootstrap-vue'
import VueCompositionAPI from '@vue/composition-api'
import * as VueGoogleMaps from 'vue2-google-maps'
import vueDebounce from 'vue-debounce'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import VueSocialSharing from 'vue-social-sharing'
import VueTelInput from 'vue-tel-input'
import constants, { CONSTANT } from '@/constants'
import i18n, { isRTLLocale } from '@/libs/i18n'
import {
  challengeTypeName, packageTypeName, systemFieldLabel, translateActivityType,
} from '@/libs/i18n/dataLabels'
import router from './router'
import store from './store'
import { initTheme } from './theme'
import App from './App.vue'
import './plugins/moengage'

import './global-components'
import '@/common/filters/filters'

import '@/libs/portal-vue'
import '@/libs/toastification'
import '@/libs/vue-chartjs'
import 'bootstrap/dist/css/bootstrap.css'
import 'bootstrap-vue/dist/bootstrap-vue.css'
import 'vue-tel-input/dist/vue-tel-input.css'
import '@fortawesome/fontawesome-free/css/all.css'

Vue.use(BootstrapVue)
Vue.use(VueTelInput)
Vue.use(VueSocialSharing)
Vue.component('ValidationProvider', ValidationProvider)
Vue.component('ValidationObserver', ValidationObserver)
Vue.prototype.constants = constants
Vue.use(vueDebounce)
Vue.use(ToastPlugin)
Vue.use(ModalPlugin)
Vue.use(CKEditor)
Vue.directive('click-outside', {
  bind(el, binding, vnode) {
    el.clickOutsideEvent = event => {
      if (
        !(el === event.target || el.contains(event.target))
        && !event.target.classList.contains('inside-navbar')
        && !event.target.parentNode.classList.contains('inside-navbar')
        && !event.target.parentElement.classList.contains('inside-navbar')
        && !event.target.classList.contains('hamburger-menu-wrapper')
        && !event.target.parentNode.classList.contains('hamburger-menu-wrapper')
        && !event.target.parentElement.classList.contains('hamburger-menu-wrapper')
      ) {
        vnode.context[binding.expression](event)
      }
    }
    document.body.addEventListener('click', el.clickOutsideEvent)
  },
  unbind(el) {
    document.body.removeEventListener('click', el.clickOutsideEvent)
  },
})

Vue.directive('click-outside-parent-element', {
  bind(el, binding, vnode) {
    el.clickOutsideEvent = function (event) {
      if (!(el === event.target || el.contains(event.target))) {
        vnode.context[binding.expression](event)
      }
    }
    document.body.addEventListener('click', el.clickOutsideEvent)
  },
  unbind(el) {
    document.body.removeEventListener('click', el.clickOutsideEvent)
  },
})
Vue.directive('click-outside-crop-image-modal', {
  bind(el, binding, vnode) {
    el.clickOutsideEvent = function (event) {
      if (
        !(el === event.target || el.contains(event.target))
        && !event.target.classList.contains('btn-img-upload')
      ) {
        vnode.context[binding.expression](event)
      }
    }
    document.body.addEventListener('click', el.clickOutsideEvent)
  },
  unbind(el) {
    document.body.removeEventListener('click', el.clickOutsideEvent)
  },
})
Vue.directive('click-outside-modal', {
  bind(el, binding, vnode) {
    el.clickOutsideModalEvent = function (event) {
      if (
        !(el == event.target || el.contains(event.target))
        && !event.target.classList.contains('multiple-detail-booking-modal')
        && event.target.parentNode.nodeName !== 'svg'
        && !event.target.classList.contains('wrapper')
      ) {
        vnode.context[binding.expression](event)
      }
    }
    document.body.addEventListener('click', el.clickOutsideModalEvent)
  },
  unbind(el) {
    document.body.removeEventListener('click', el.clickOutsideModalEvent)
  },
})
Vue.use(VueCompositionAPI)

Vue.use(VueGoogleMaps, {
  load: {
    key: process.env.VUE_APP_GOOGLE_MAP_KEY,
    libraries: 'places',
    // Localizes Google's own UI and place suggestions. The Maps JS API reads
    // this once at load, so it follows the locale the page booted with.
    language: i18n.locale,
  },
})

Vue.mixin({
  provide() {
    return {
      $i18n: this.$i18n,
    }
  },
})

Vue.mixin({
  data() {
    return {
      CONSTANT,
    }
  },
  computed:{
    // Derived from the active locale so switching language re-renders every
    // direction-aware component. Previously a `data` snapshot taken at boot,
    // which meant it never updated.
    isRTL() {
      return isRTLLocale(this.$i18n ? this.$i18n.locale : undefined)
    },
    utmParams(){
      const utmParams = CONSTANT.storedUTMParams();
      if(utmParams){
        return { ...utmParams };
      }
      else {
        return {};
      }
    }
  },
  methods: {
    // Activity type names arrive from the API. Standard catalogue entries get a
    // translation, admin-created names pass through unchanged.
    // See src/libs/i18n/dataLabels.js.
    activityTypeName(name) {
      return translateActivityType(name)
    },
    // Registration field names are an API contract; only the label is localized.
    // See src/libs/i18n/dataLabels.js.
    systemFieldLabel(name) {
      return systemFieldLabel(name)
    },
    // Package type names are API-owned; only the label is localized.
    packageTypeName(name) {
      return packageTypeName(name)
    },
    // Challenge type names are API-owned; only the label is localized.
    challengeTypeName(name) {
      return challengeTypeName(name)
    },
  },
})

require('@core/scss/core.scss')
require('@/assets/scss/style.scss')
// Extends the brand font family with a Naskh face for Arabic-script glyphs.
require('@/assets/scss/arabic-typography.scss')
// Loaded last so its [dir="rtl"] overrides win over the base stylesheets.
require('@/assets/scss/rtl.scss')

Vue.config.productionTip = false

// Before mount, so the first render already has the saved accent and mode.
initTheme(store)

// Detect the user's currency and warm the exchange-rate cache.
// Non-blocking: prices render in USD until rates arrive, then re-render.
store.dispatch('currency/initialize')

new Vue({
  router,
  store,
  i18n,
  render: h => h(App),
}).$mount('#app')
